type WaitingRead = {
  start: () => void;
  reject: (error: Error) => void;
  priority: "normal" | "interactive";
};

/** Allow short interactive bursts without permitting unbounded polling. */
export class RpcReadQueue {
  private waiting: WaitingRead[] = [];
  private started: number[] = [];
  private active = 0;
  private nextStart = 0;
  private cooldownUntil = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private closed = false;

  constructor(
    private readonly policy: {
      concurrent: number;
      intervalMs: number;
      requestsPerWindow: number;
      windowMs: number;
      interactiveReserve?: number;
    } = {
      concurrent: 4,
      // Let independent reads start together. Concurrency, the rolling budget,
      // and provider cooldowns still bound every burst.
      intervalMs: 0,
      requestsPerWindow: 180,
      windowMs: 60_000,
      interactiveReserve: 0,
    },
  ) {}

  run<T>(
    read: () => Promise<T>,
    options: { priority?: "normal" | "interactive"; signal?: AbortSignal } = {},
  ): Promise<T> {
    if (this.closed)
      return Promise.reject(new Error("App RPC provider was closed"));
    return new Promise<T>((resolve, reject) => {
      const cleanup = () => options.signal?.removeEventListener("abort", abort);
      const abort = () => {
        const index = this.waiting.indexOf(task);
        if (index < 0) return;
        this.waiting.splice(index, 1);
        cleanup();
        reject(Object.assign(new Error("Cancelled"), { name: "AbortError" }));
        this.pump();
      };
      const task: WaitingRead = {
        priority: options.priority ?? "normal",
        reject: (error) => {
          cleanup();
          reject(error);
        },
        start: () => {
          cleanup();
          this.active++;
          void Promise.resolve()
            .then(read)
            .then(resolve, reject)
            .finally(() => {
              this.active--;
              this.pump();
            });
        },
      };
      this.waiting.push(task);
      if (options.signal?.aborted) {
        abort();
        return;
      }
      options.signal?.addEventListener("abort", abort, { once: true });
      this.pump();
    });
  }

  cooldown(delayMs: number): void {
    this.cooldownUntil = Math.max(
      this.cooldownUntil,
      performance.now() + delayMs,
    );
    this.pump();
  }

  private pump(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    if (
      this.closed ||
      !this.waiting.length ||
      this.active >= this.policy.concurrent
    )
      return;
    const now = performance.now();
    // Reserve is part of the same total budget, never an extra allowance.
    const interactive = this.waiting.findIndex(
      (task) => task.priority === "interactive",
    );
    if (interactive > 0)
      this.waiting.unshift(this.waiting.splice(interactive, 1)[0]);
    this.started = this.started.filter((at) => at > now - this.policy.windowMs);
    const limit =
      this.policy.requestsPerWindow -
      (this.waiting[0].priority === "normal"
        ? (this.policy.interactiveReserve ?? 0)
        : 0);
    const budgetReady =
      this.started.length >= limit
        ? this.started[this.started.length - limit] + this.policy.windowMs
        : now;
    const delay =
      Math.max(this.nextStart, this.cooldownUntil, budgetReady) - now;
    if (!Number.isFinite(delay) || delay > 120_000) {
      for (const task of this.waiting.splice(0))
        task.reject(
          new Error(
            "Chain reads are rate limited. Wait for the provider cooldown, then retry.",
          ),
        );
      return;
    }
    if (delay > 0) {
      this.timer = setTimeout(() => this.pump(), delay);
      return;
    }
    this.started.push(now);
    this.nextStart = now + this.policy.intervalMs;
    this.waiting.shift()!.start();
    this.pump();
  }

  close(): void {
    this.closed = true;
    clearTimeout(this.timer);
    for (const task of this.waiting.splice(0))
      task.reject(new Error("App RPC provider was closed"));
  }
}
